import React from 'react';
import './index.scss';
import { Section } from '../../../components/shared/Section';
import TransitionIn from '../../../components/shared/TransitionIn';

const FeeSchedule = props => {
    const heading = "Fee Schedule";

    const body = (
        <article className="fee-schedule">
            <table>
                <caption>Payment Processing</caption>
                <thead>
                    <tr>
                        <th>Type</th>
                        <th>Fee Description</th>
                        <th>Amount Charged</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Recurring ACH</td>
                        <td>Payment processing fees are not assessed for monthly payments made via recurring ACH.</td>
                        <td>Free</td>
                    </tr>
                    <tr>
                        <td>Mandatory Recurring ACH Not Maintained</td>
                        <td>A monthly fee is assessed when mandated recurring ACH payments are not established or maintained as specified in the loan package.</td>
                        <td>$100 (UPB &lt; $1M)<br/>$500 (UPB $1M to $5M)<br/>$1,000 (UPB &gt; $5M)</td>
                    </tr>
                    <tr>
                        <td>Online Payment</td>
                        <td>A processing fee is assessed for “one time” ACH payments initiated through the online portal.</td>
                        <td>$3.95</td>
                    </tr>
                    <tr>
                        <td>Agent-Assisted Payment</td>
                        <td>A processing fee for agent assisted payments via email or phone.</td>
                        <td>$9.95</td>
                    </tr>
                    <tr>
                        <td>Non-Sufficient Funds (NSF)</td>
                        <td>A charge is assessed when a payment is returned due to insufficient funds.</td>
                        <td>$25–$40   *Refer to Note</td>
                    </tr>
                    <tr>
                        <td>Late Charges</td>
                        <td>A late charge is assessed when a payment is received after the designated grace period.</td>
                        <td>*Refer to Note</td>
                    </tr>
                </tbody>
            </table>

            <table>
                <caption>Payoff Letter Requests</caption>
                <thead>
                    <tr>
                        <th>Type</th>
                        <th>Fee Description</th>
                        <th>Amount Charged</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>First Request</td>
                        <td>A fee to cover the cost of generating a payoff letter upon first request.</td>
                        <td>$10</td>
                    </tr>
                    <tr>
                        <td>Repeat Request</td>
                        <td>A fee when a payoff letter is requested multiple times within 30 days of the original request.</td>
                        <td>$30</td>
                    </tr>
                    <tr>
                        <td>Rush Request</td>
                        <td>A fee when a payoff letter is requested on an expedited basis. It will cover the cost of prioritizing and processing the request urgently.</td>
                        <td>$100</td>
                    </tr>
                </tbody>
            </table>

            <table>
                <caption>Lien Release</caption>
                <thead>
                    <tr>
                        <th>Type</th>
                        <th>Fee Description</th>
                        <th>Amount Charged</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Recording Cost</td>
                        <td>A fee to release or discharge a Lien recorded with a county. The fee amount may vary by County.</td>
                        <td>$5-$700</td>
                    </tr>
                </tbody>
            </table>

            <table>
                <caption>Escrow Advances</caption>
                <thead>
                    <tr>
                        <th>Type</th>
                        <th>Fee Description</th>
                        <th>Amount Charged</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Tax Advances</td>
                        <td>A fee is assessed when the lender pays delinquent property taxes on behalf of the borrower.</td>
                        <td>$85</td>
                    </tr>
                </tbody>
            </table>

            <table>
                <caption>Construction Draws</caption>
                <thead>
                    <tr>
                        <th>Type</th>
                        <th>Fee Description</th>
                        <th>Amount Charged</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Draw Inspection (Single Property Loan)</td>
                        <td>A draw inspection fee will be assessed as part of the evaluation of the project and remittance of each draw. This fee applies to single-asset loans, per draw.</td>
                        <td>$275 per draw</td>
                    </tr>
                    <tr>
                        <td>Draw Inspection (Multi-Property Loan)</td>
                        <td>A draw inspection fee will be assessed as part of the evaluation of the project and remittance of each draw. This fee applies to each property within a multi-property loan, per draw.</td>
                        <td>$175 per building</td>
                    </tr>
                </tbody>
            </table>

            <table>
                <caption>Loan Extensions & Modifications</caption>
                <thead>
                    <tr>
                        <th>Type</th>
                        <th>Fee Description</th>
                        <th>Amount Charged</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Extension Fee</td>
                        <td>A fee required to extend the maturity date of a loan by an additional three months.</td>
                        <td>*Refer to Note</td>
                    </tr>
                    <tr>
                        <td>Loan Recast</td>
                        <td>A loan recast allows borrowers to reduce their monthly payment by applying a lump sum toward the principal balance of their loan, resulting in a re-amortization of the remaining balance over the original loan term.</td>
                        <td>$500</td>
                    </tr>
                </tbody>
            </table>

            <table>
                <caption>Inspections & Appraisals</caption>
                <thead>
                    <tr>
                        <th>Type</th>
                        <th>Fee Description</th>
                        <th>Amount Charged</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Inspection (Prop. Pres.)</td>
                        <td>A fee when evaluating the condition and occupancy status of a property. This is to ensure compliance with maintenance standards.</td>
                        <td>$200</td>
                    </tr>
                    <tr>
                        <td>Inspection (Disaster)</td>
                        <td>A fee when evaluating the condition and occupancy status of a property following a natural disaster. Fee amount will vary by location.</td>
                        <td>$35-$135</td>
                    </tr>
                    <tr>
                        <td>BPO</td>
                        <td>A BPO (Broker Price Opinion appraisal) is a real estate valuation method typically performed by a licensed real estate broker or agent. It’s used to estimate the value of a property.</td>
                        <td>$90-$110</td>
                    </tr>
                    <tr>
                        <td>Title Search</td>
                        <td>A title search is a comprehensive review of public records conducted to confirm the legal ownership of a property and uncover any issues that might affect that ownership.</td>
                        <td>$75-$150</td>
                    </tr>
                    <tr>
                        <td>Appraisal</td>
                        <td>An appraisal is a formal, detailed assessment of a property's market value conducted by a licensed or certified appraiser.</td>
                        <td>$20-$5,000+</td>
                    </tr>
                </tbody>
            </table>

            <table>
                <caption>CEMA</caption>
                <thead>
                    <tr>
                        <th>Type</th>
                        <th>Fee Description</th>
                        <th>Amount Charged</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Processing Fee</td>
                        <td>The CEMA processing fee is a fee to initiate the Consolidation, Extension, and Modification Agreement (CEMA) process in New York real estate transactions. This process allows borrowers to reduce their mortgage recording tax by modifying an existing loan rather than creating a new one.</td>
                        <td>$750</td>
                    </tr>
                </tbody>
            </table>

            <table>
                <caption>Other</caption>
                <thead>
                    <tr>
                        <th>Type</th>
                        <th>Fee Description</th>
                        <th>Amount Charged</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Exit Fee</td>
                        <td>A fee assessed when a loan is not paid off by the original maturity date and does not secure a timely extension of the loan term.</td>
                        <td>1.5% of the Original Loan Amount</td>
                    </tr>
                    <tr>
                        <td>Legal Costs</td>
                        <td>Legal fees incurred by the lender during the loan process may be passed on to the borrower as part of the closing costs or loan-related expenses.</td>
                        <td>*Refer to Note</td>
                    </tr>
                </tbody>
            </table>
        </article>
    )

    return (
        <TransitionIn content={ <Section heading={heading} body={body} /> } />
    )

}

export default FeeSchedule